import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "./App.css";
import Landing from "./pages/Landing";
import ExpenseIntel from "./pages/ExpenseIntel";
import Admin from "./pages/Admin";
import BookingManage from "./pages/BookingManage";
import ProgressShare from "./pages/ProgressShare";
import JobProgressPublic from "./pages/JobProgressPublic";
import PartnerPortal from "./pages/PartnerPortal";
import { PaymentSuccess, PaymentCancel } from "./pages/PaymentStatus";
import PortalLogin from "./pages/PortalLogin";
import PortalDashboard from "./pages/PortalDashboard";
import PortalWelcome from "./pages/PortalWelcome";
import PortalCallback from "./pages/PortalCallback";
import PortalReset from "./pages/PortalReset";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import SmsTerms from "./pages/SmsTerms";
import Plans from "./pages/Plans";
import QuoteAccept from "./pages/QuoteAccept";
import PortalProtectedRoute from "./components/site/PortalProtectedRoute";
import CookieBanner from "./components/site/CookieBanner";
import { PortalAuthProvider } from "./context/PortalAuth";

function SmoothScroll() {
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;
    const lenis = new Lenis({ duration: 1.1, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // OAuth callback: detect session_id in the URL fragment during render,
  // BEFORE ProtectedRoute runs, to avoid race conditions.
  if (location.hash?.includes("session_id=")) {
    return <PortalCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/expenses" element={<ExpenseIntel />} />
      <Route path="/booking/manage/:id" element={<BookingManage />} />
      <Route path="/progress/:token" element={<ProgressShare />} />
      <Route path="/job/:token" element={<JobProgressPublic />} />
      <Route path="/partner/:token" element={<PartnerPortal />} />
      <Route path="/partner-portal/:token" element={<PartnerPortal />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal/reset" element={<PortalReset />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/sms-terms" element={<SmsTerms />} />
      <Route path="/plans" element={<Plans />} />
      <Route path="/quote/:id" element={<QuoteAccept />} />
      <Route
        path="/portal"
        element={
          <PortalProtectedRoute>
            <PortalDashboard />
          </PortalProtectedRoute>
        }
      />
      <Route
        path="/portal/welcome"
        element={
          <PortalProtectedRoute>
            <PortalWelcome />
          </PortalProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <div className="noise-overlay" />
      <BrowserRouter>
        <PortalAuthProvider>
          <SmoothScroll />
          <Toaster theme="dark" position="bottom-right" richColors />
          <AppRouter />
          <CookieBanner />
        </PortalAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
